import {
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
} from "@material-ui/core";
import React, {Dispatch, SetStateAction, useState} from "react";
import FlexBox from "view/components/Layout/FlexBox";
import TextField from "@material-ui/core/TextField";
import {Currency, IStage, SurchargeType} from "@evennt-tech/api-v2-core";
import Alert from "view/components/Alert";

import useStyles from "./style";

type AddProps = {
  type: "add";
};

type EditProps = {
  type: "edit";
  surcharge: IStage;
};

type AddOrEditProps = (EditProps | AddProps) & {
  onCancel: () => void;
  onSubmit: (stage: IStage) => void;
  currency: Currency;
  isUnpublishTaken: boolean;
  takenDates: number[];
};

function AddOrEdit({
  onCancel,
  onSubmit,
  currency,
  takenDates,
  isUnpublishTaken,
  ...props
}: AddOrEditProps) {
  const classes = useStyles();

  const [stage, setStage] = useState<IStage>(
    props.type === "add"
      ? ({type: SurchargeType.percentage} as IStage)
      : props.surcharge
  );

  const isErrorDays = takenDates.includes(stage.daysBeforeBuild);

  const isSubmissionDisabled =
    isErrorDays ||
    !stage.daysBeforeBuild ||
    (stage.type !== SurchargeType.unpublish && !stage.amount);

  return (
    <FlexBox column gap={2} width="100%">
      <span className={classes.title}>
        {props.type === "edit" ? "Edit" : "Add New"} Surcharge
      </span>
      <FlexBox column gap={2} className={classes.form}>
        <FormControl variant="outlined">
          <InputLabel>Select Type *</InputLabel>
          <Select
            fullWidth
            labelId="demo-simple-select-outlined-label"
            id="demo-simple-select-outlined"
            value={stage.type}
            onChange={({target}) =>
              setStage((old) => ({...old, type: target.value} as IStage))
            }
            label="Select Type *"
          >
            <MenuItem value={SurchargeType.percentage}>Percentage</MenuItem>
            <MenuItem value={SurchargeType.fixedFee}>Fixed Fee</MenuItem>
            <MenuItem
              disabled={isUnpublishTaken}
              value={SurchargeType.unpublish}
            >
              Unpublish Product
            </MenuItem>
          </Select>
        </FormControl>
        <Amount stage={stage} setStage={setStage} currency={currency} />
        <FlexBox column gap={0.75}>
          <TextField
            fullWidth
            type="text"
            label="Days before Build Up starts"
            required
            error={isErrorDays}
            variant="outlined"
            value={stage.daysBeforeBuild}
            onChange={({target}) =>
              setStage((old) => ({
                ...old,
                daysBeforeBuild: composeValue(
                  old.daysBeforeBuild,
                  target.value
                ),
              }))
            }
          />
          <span className={classes.daysBeforeTip}>
            Set date for action to become active
          </span>
        </FlexBox>
        {isErrorDays && (
          <Alert severity="error">
            The surcharge for this time was already set
          </Alert>
        )}
        <FlexBox alignCenter justifyContent="end">
          <Button color="primary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            disabled={isSubmissionDisabled}
            color="primary"
            onClick={() => onSubmit(stage)}
          >
            Submit
          </Button>
        </FlexBox>
      </FlexBox>
    </FlexBox>
  );
}

type AmountProps = {
  stage: IStage;
  setStage: Dispatch<SetStateAction<IStage>>;
  currency: Currency;
};

function Amount({stage, setStage, currency}: AmountProps) {
  const classes = useStyles();
  if (stage.type === SurchargeType.unpublish) {
    return null;
  }
  if (stage.type === SurchargeType.fixedFee) {
    return (
      <FormControl fullWidth variant="outlined">
        <InputLabel htmlFor="outlined-adornment-amount">Amount</InputLabel>
        <OutlinedInput
          fullWidth
          id="outlined-adornment-amount"
          type="text"
          value={stage.amount}
          onChange={({target}) => {
            if (target.value.includes("-")) {
              return;
            }
            setStage((old) => ({
              daysBeforeBuild: old.daysBeforeBuild,
              type: SurchargeType.fixedFee,
              amount: composeValue(stage.amount || 0, target.value, true),
            }));
          }}
          startAdornment={
            <InputAdornment position="start">
              <span className={classes.currencyTitle}>{currency}</span>
            </InputAdornment>
          }
          labelWidth={68}
        />
      </FormControl>
    );
  }
  return (
    <FormControl variant="outlined">
      <InputLabel htmlFor="outlined-adornment-password">Amount</InputLabel>
      <OutlinedInput
        fullWidth
        id="outlined-adornment-password"
        type="text"
        value={stage.amount}
        onChange={({target}) =>
          setStage((old) => ({
            daysBeforeBuild: old.daysBeforeBuild,
            type: SurchargeType.percentage,
            amount: composeValue(stage.amount || 0, target.value, true),
          }))
        }
        endAdornment={<InputAdornment position="end">%</InputAdornment>}
        labelWidth={68}
      />
    </FormControl>
  );
}

const decimalNumsOnlyRegex = /^\d+(\.)?(\d+)?$/;
const numsOnlyRegex = /^\d+$/;
const composeValue = (old: number, value: string, allowDecimal = false) => {
  if (!value) {
    // NOTE: We need it to pass empty ("") value
    return ("" as unknown) as number;
  }
  const isNumber = allowDecimal
    ? decimalNumsOnlyRegex.test(value)
    : numsOnlyRegex.test(value);
  if (isNumber) {
    return allowDecimal ? ((value as unknown) as number) : Number(value);
  }
  return old || (("" as unknown) as number);
};

export default AddOrEdit;
